import React, { useState } from 'react';
import { emptyGroup, emptyPair } from '../CalculationBuilder';
import AppSelect, { useAppSelectDismiss } from './AppSelect';

export function budgetCatalog(lines) {
  const codes = new Map();
  const types = new Map();
  const combinations = new Map();
  (Array.isArray(lines) ? lines : []).forEach((line) => {
    const code = line.cost_code;
    const type = line.cost_type;
    if (code?.vuid && !codes.has(code.vuid)) {
      codes.set(code.vuid, {
        vuid: code.vuid,
        code: code.code || '',
        description: code.description || '',
      });
    }
    if (type?.vuid && !types.has(type.vuid)) types.set(type.vuid, type);
    if (code?.vuid && type?.vuid) {
      const key = `${code.vuid}|${type.vuid}`;
      if (!combinations.has(key)) {
        combinations.set(key, {
          key,
          cost_code_vuid: code.vuid,
          cost_type_vuid: type.vuid,
          code: code.code || '',
          description: code.description || '',
          cost_type: type.cost_type || '',
          abbreviation: type.abbreviation || '',
        });
      }
    }
  });
  return {
    costCodes: [...codes.values()].sort((a, b) => String(a.code).localeCompare(String(b.code))),
    costTypes: [...types.values()].sort((a, b) => String(a.cost_type || a.abbreviation || '').localeCompare(String(b.cost_type || b.abbreviation || ''))),
    combinations: [...combinations.values()].sort((a, b) => {
      const byCode = String(a.code).localeCompare(String(b.code));
      if (byCode) return byCode;
      return String(a.abbreviation || a.cost_type).localeCompare(String(b.abbreviation || b.cost_type));
    }),
  };
}

function combinationPrimary(row) {
  const flat = row.abbreviation ? `${row.code}.${row.abbreviation}` : (row.code || '');
  return row.description ? `${flat} — ${row.description}` : flat;
}

function combinationSecondary(row) {
  if (!row.cost_type) return '';
  if (row.abbreviation && row.cost_type === row.abbreviation) return '';
  return row.cost_type;
}

function selectedCombinationLabel(combinations, codeVuid, typeVuid, codeLabel, typeLabel) {
  const match = combinations.find((row) => row.cost_code_vuid === codeVuid && row.cost_type_vuid === typeVuid);
  if (match) return combinationPrimary(match);
  return [codeLabel, typeLabel].filter(Boolean).join(' · ');
}

function costTypeLabel(costTypes, vuid) {
  const type = costTypes.find((row) => row.vuid === vuid);
  if (!type) return '';
  if (type.abbreviation && type.cost_type && type.abbreviation !== type.cost_type) {
    return `${type.cost_type} (${type.abbreviation})`;
  }
  return type.cost_type || type.abbreviation || '';
}

export default function SourceScopeEditor({
  costTypes,
  budgetCombinations = [],
  groups,
  expandedGroupIds,
  setGroups,
  setExpandedGroupIds,
  intro,
  readOnly = false,
  showSources = true,
  showDestination = false,
  destinationHint = 'Where the resulting job cost for this group is recorded.',
  allowAdd = true,
  filterOptional = false,
}) {
  const [openPicker, setOpenPicker] = useState('');
  useAppSelectDismiss(setOpenPicker);
  const combinationOptions = budgetCombinations.map((row) => ({
    vuid: row.key,
    primary: combinationPrimary(row),
    secondary: combinationSecondary(row),
  }));
  const applyCombination = (row) => ({
    cost_code_vuid: row?.cost_code_vuid || '',
    cost_type_vuid: row?.cost_type_vuid || '',
    cost_code_label: row ? `${row.code || ''} — ${row.description || ''}`.replace(/ — $/, '') : '',
    cost_type_label: row ? (row.abbreviation || row.cost_type || '') : '',
  });
  const applyDestination = (row) => {
    const picked = applyCombination(row);
    return {
      destination_cost_code_vuid: picked.cost_code_vuid,
      destination_cost_type_vuid: picked.cost_type_vuid,
      destination_cost_code_label: picked.cost_code_label,
      destination_cost_type_label: picked.cost_type_label,
    };
  };

  const toggleGroup = (id) => {
    setExpandedGroupIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const updateGroup = (id, patch) => {
    setGroups((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  return (
    <section id={showDestination ? 'project-cost-destination' : undefined} className="bg-white border border-gray-200 rounded-md p-4 mb-3">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-semibold text-gray-900">{showSources && showDestination ? 'Calculation Groups' : (showSources ? (filterOptional && !groups.length ? 'Included cost codes' : 'Which costs should be included?') : 'Where should the calculated project cost go?')}</h2>
        {readOnly || !allowAdd ? null : (
        <button
          type="button"
          className="btn-claret"
          onClick={() => {
            const group = emptyGroup();
            setGroups((rows) => [...rows, group]);
            setExpandedGroupIds((current) => new Set(current).add(group.id));
          }}
        >
          {filterOptional && !groups.length ? 'Limit to selected cost codes' : 'Add another group'}
        </button>
        )}
      </div>
      <p className="text-sm text-gray-600 mb-4">{intro}</p>
      {budgetCombinations.length === 0 ? (
        <p className="text-sm text-amber-800 mb-3">This project's budget has no cost code and cost type combinations yet.</p>
      ) : null}
      {groups.map((group, groupIndex) => {
        const destinationMissing = showDestination && !readOnly && !group.destination_cost_code_vuid;
        const expanded = expandedGroupIds.has(group.id);
        const pairCount = group.pairs.filter((pair) => pair.cost_code_vuid && pair.cost_type_vuid).length;
        const title = group.label
          ? `Group ${groupIndex + 1} — ${group.label}`
          : `Group ${groupIndex + 1}`;
        const destination = [
          group.destination_cost_code_label,
          group.destination_cost_type_label ? `Cost Type: ${group.destination_cost_type_label}` : '',
        ].filter(Boolean).join(' · ');
        return (
          <div key={group.id} className="border border-gray-200 rounded-md mb-4">
            <div className="flex items-center justify-between gap-3 px-4 py-3">
              <button type="button" className="flex-1 text-left" aria-expanded={expanded || destinationMissing} onClick={() => toggleGroup(group.id)}>
                <span className="font-medium text-gray-900">{showSources ? title : 'Destination'}</span>
                <span className="mt-1 block text-sm text-gray-600">
                  {showSources ? `Calculate from: ${pairCount} cost code / cost type combination${pairCount === 1 ? '' : 's'}` : null}
                  {showDestination ? `${showSources ? ' · ' : ''}Post project cost to: ${destination || 'Not set'}` : null}
                </span>
              </button>
              <button type="button" className="text-sm underline" onClick={() => toggleGroup(group.id)}>
                {expanded ? 'Hide' : (readOnly ? 'View' : 'View / Edit')}
              </button>
              {readOnly || !allowAdd ? null : (
              <button
                type="button"
                className="text-sm underline"
                onClick={() => {
                  setGroups((rows) => rows.filter((item) => item.id !== group.id));
                  setExpandedGroupIds((current) => {
                    const next = new Set(current);
                    next.delete(group.id);
                    return next;
                  });
                }}
              >
                Remove
              </button>
              )}
            </div>
            {showDestination && (expanded || destinationMissing) ? (
              <div className="px-4 pb-4">
                {showDestination ? (
                  <div className={showSources ? 'mb-4 border-b border-gray-200 pb-3' : ''}>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">Post project cost to</h3>
                    <p className="text-xs text-gray-500 mb-2">{destinationHint}</p>
                    <AppSelect
                      id={`${group.id}-destination`}
                      open={openPicker === `${group.id}-destination`}
                      setOpen={setOpenPicker}
                      value={selectedCombinationLabel(
                        budgetCombinations,
                        group.destination_cost_code_vuid,
                        group.destination_cost_type_vuid,
                        group.destination_cost_code_label,
                        group.destination_cost_type_label || costTypeLabel(costTypes, group.destination_cost_type_vuid),
                      )}
                      placeholder="Search cost code and type..."
                      searchPlaceholder="Search cost code and type..."
                      options={combinationOptions}
                      layout="stacked"
                      menuClassName="w-full"
                      disabled={readOnly}
                      onSelect={(key) => {
                        const selected = budgetCombinations.find((row) => row.key === key);
                        updateGroup(group.id, applyDestination(selected));
                      }}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
            {expanded ? (
              <div className="px-4 pb-4">
                {showSources ? (
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Group label</label>
                  <input
                    className="px-3 py-2 border border-gray-300 rounded-md w-full"
                    placeholder={`Group ${groupIndex + 1} name, such as Field Labor`}
                    value={group.label}
                    disabled={readOnly}
                    onChange={(event) => updateGroup(group.id, { label: event.target.value })}
                  />
                </div>
                ) : null}
                {showSources ? (
                  <div className="mb-2">
                    <h3 className="text-sm font-semibold text-gray-900">Calculate from</h3>
                    <p className="text-xs text-gray-500">Only costs matching this selection are included in the calculation.</p>
                  </div>
                ) : null}
                {showSources ? group.pairs.map((pair, pairIndex) => (
                  <div key={`${group.id}-${pairIndex}`} className="flex items-start gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <AppSelect
                        id={`${group.id}-source-${pairIndex}`}
                        open={openPicker === `${group.id}-source-${pairIndex}`}
                        setOpen={setOpenPicker}
                        value={selectedCombinationLabel(budgetCombinations, pair.cost_code_vuid, pair.cost_type_vuid, pair.cost_code_label, pair.cost_type_label)}
                        placeholder="Search cost code and type..."
                        searchPlaceholder="Search cost code and type..."
                        options={combinationOptions}
                        layout="stacked"
                        menuClassName="w-full"
                        disabled={readOnly}
                        onSelect={(key) => {
                          const selected = budgetCombinations.find((row) => row.key === key);
                          const pairs = group.pairs.map((item, index) => (
                            index === pairIndex ? { ...item, ...applyCombination(selected) } : item
                          ));
                          updateGroup(group.id, { pairs });
                        }}
                      />
                    </div>
                    {readOnly ? null : (
                    <button
                      type="button"
                      className="text-sm underline text-left mt-2"
                      onClick={() => updateGroup(group.id, { pairs: group.pairs.filter((_, index) => index !== pairIndex) })}
                    >
                      Remove
                    </button>
                    )}
                  </div>
                )) : null}
                {readOnly || !showSources ? null : (
                <button
                  type="button"
                  className="text-sm underline"
                  onClick={() => updateGroup(group.id, { pairs: [...group.pairs, emptyPair()] })}
                >
                  Add cost code / cost type
                </button>
                )}
              </div>
            ) : null}
          </div>
        );
      })}
    </section>
  );
}

export function slicesFromGroups(groups) {
  return groups.map((group) => ({
    label: group.label,
    pairs: (group.pairs || []).map((pair) => ({
      cost_code_vuid: pair.cost_code_vuid || '',
      cost_type_vuid: pair.cost_type_vuid || '',
      cost_code_label: pair.cost_code_label || '',
      cost_type_label: pair.cost_type_label || '',
    })),
    destination_cost_code_vuid: group.destination_cost_code_vuid || '',
    destination_cost_type_vuid: group.destination_cost_type_vuid || '',
  }));
}
